import React from "react";
import { createPortal } from "react-dom";
import "./NotificationsOffPopup.css";
import "./InstantIntroSheet.css";
import "./InstantPayoutsPopup.css";
import { DebitCardArt } from "./InstantIntroSheet";
import { t } from "../i18n";
import { T } from "../i18n/T";

// What the Instant Payouts row on Balance opens (founder, 1.5.1 #8). The row
// only EXPLAINS now: it used to start a cash-out while it said "Ready", and
// the green Withdraw button is the one way money leaves. The notifications
// popup's card (NotificationsOffPopup.css) topped with the red DEBIT card and
// gold "~30 MIN" chip of "Instant payouts are here" (InstantIntroSheet) —
// founder, TestFlight 1.5.2. All three states share it: ready prints the
// talent's own last four, needsCard the sample card, locked dims it. The card
// settles in once; with Reduce Motion it fades in where it stands.
// Portaled to <body>: the Balance page is a clipped one-screen box, so a
// fixed overlay inside it would stack under the tab bar.

export type InstantPopupState = "ready" | "needsCard" | "locked";

type Props = {
  state: InstantPopupState;
  /** ready: where instant money lands, as the row shows it ("Visa •••4242"). */
  destination: string;
  /** locked: the minimum, already formatted in the payout currency ("$5"). */
  minimum: string;
  /** The last four printed on the card art (ready / locked); the sample card otherwise. */
  last4?: string | null;
  /** Instant money lands on a bank account, not a card: no DEBIT word on the art. */
  toBank?: boolean;
  /** needsCard: what the row itself used to do — the in-app card sheet, or Stripe. */
  onAddCard: () => void;
  onClose: () => void;
};

const bold = (c: string) => <b>{c}</b>;

export default function InstantPayoutsPopup({ state, destination, minimum, last4, toBank, onAddCard, onClose }: Props) {
  const own = state !== "needsCard";
  return createPortal(
    <div className="notifoff-overlay instant-pop" role="dialog" aria-modal="true" aria-labelledby="instant-pop-title">
      <div className="notifoff-card" data-state={state}>
        <div className={`ipop-hero${state === "locked" ? " ipop-hero--dim" : ""}`} aria-hidden="true">
          <div className="iis-card">
            <DebitCardArt width={168} last4={own && last4 ? last4 : undefined} mark={!(own && toBank)} />
            <span className="iis-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M13.5 2 4.5 13.5H11L10 22l9-11.5h-6.5L13.5 2Z" />
              </svg>
              {t("instantIntroSheet.chip")}
            </span>
          </div>
        </div>
        <div className="notifoff-title" id="instant-pop-title">
          {state === "ready"
            ? t("instantPayoutsPopup.youReAllSet")
            : state === "needsCard"
              ? t("instantPayoutsPopup.getPaidIn30")
              : t("instantPayoutsPopup.almostThere")}
        </div>
        <p className="notifoff-sub">
          {state === "ready" ? (
            <T k="instantPayoutsPopup.whenYouCashOut" vars={{ destination }} tags={{ 0: bold }} />
          ) : state === "needsCard" ? (
            t("instantPayoutsPopup.addYourDebitCard")
          ) : (
            <T k="instantPayoutsPopup.startAt" vars={{ amount: minimum }} tags={{ 0: bold }} />
          )}
        </p>
        {state === "needsCard" ? (
          <>
            <button type="button" className="notifoff-cta" onClick={onAddCard}>
              {t("instantPayoutsPopup.addDebitCard")}
            </button>
            <button type="button" className="notifoff-ghost" onClick={onClose}>
              {t("instantPayoutsPopup.notNow")}
            </button>
          </>
        ) : (
          <button type="button" className="notifoff-cta" onClick={onClose}>
            {t("instantPayoutsPopup.gotIt")}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
